import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useState, useEffect } from "react";
import { FileEdit, Plus } from "lucide-react";
import Select from "react-select";
import { useAppServices } from "hook/services";

interface propTypes {
  handleRefresh: any;
  toggle: any;
  show: boolean;
  editData: {
    _id: string;
    name: string;
    value: string;
    fieldKey: string;
    parentId: string;
  };
  folders: any[];
}

const CustomValueEditModal = ({
  handleRefresh,
  show,
  toggle,
  editData,
  folders,
}: propTypes) => {
  const AppService = useAppServices();

  const handleSubmit = async (values: any) => {
    const payload = {
      name: values.name.trim(),
      value: values.value.trim(),
      fieldKey: `customValue.${values.name.toLowerCase().replace(/\s+/g, "_")}`,
      parentId: values.parentId || null,
      _id: editData._id,
    };
    const { response, error } = await AppService.custom_value.value.update({
      payload,
      toaster: true,
    });
    if (response) {
      toggle();
      handleRefresh();
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editData?.name || "",
      value: editData?.value || "",
      parentId: editData?.parentId || "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Please enter field name")
        .min(2, "Must be at least 2 characters")
        .max(100, "Must be less than 100 characters"),
      value: Yup.string()
        .required("Please enter field value")
        .min(2, "Must be at least 2 characters")
        .max(200, "Must be less than 200 characters"),
      parentId: Yup.string().nullable(),
    }),
    onSubmit: handleSubmit,
  });

  const folderOptions =
    folders?.map((f: any) => ({
      value: f._id,
      label: f.name,
    })) || [];

  return (
    <>
      <Modal
        show={show}
        onHide={toggle}
        id="customvalueModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[40rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
          <Modal.Title className="text-16 font-semibold">
            Edit CustomValue
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block mb-1 text-sm font-medium">Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter field name"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
              />
              {formik.touched.name && formik.errors.name && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.name}
                </p>
              )}
            </div>

            {/* Value */}
            <div>
              <label className="block mb-1 text-sm font-medium">Value</label>
              <input
                type="text"
                name="value"
                placeholder="Enter field value"
                className="form-input w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.value}
              />
              {formik.touched.value && formik.errors.value && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.value}
                </p>
              )}
            </div>

            {/* Folder (ParentId) */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Folder (optional)
              </label>
              <Select
                name="parentId"
                options={folderOptions}
                placeholder="Select folder"
                value={
                  formik.values.parentId
                    ? folderOptions.find(
                        (opt) => opt.value === formik.values.parentId
                      )
                    : null
                }
                onChange={(option) =>
                  formik.setFieldValue("parentId", option?.value ?? "")
                }
                onBlur={() => formik.setFieldTouched("parentId", true)}
                isClearable
              />
              {formik.touched.parentId && formik.errors.parentId && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.parentId}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 bg-white border btn hover:text-red-600"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
              >
                Edit Custom Value
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CustomValueEditModal;
